import { Box, Link, Text, VStack } from '@chakra-ui/react'

import { PageSection } from '../components/pageSection'

export function TermsOfUsePage() {
  const updatedDate = '24 Jul 2024'

  return (
    <>
      <PageSection backgroundColor="base.canvas.brand-subtle">
        <Box width="100%" paddingX={{ base: '0px', lg: '55px', xl: '100px' }}>
          <Box maxW="550px" paddingY="112px">
            <Text textStyle="h1" marginBottom="16px" color="base.content.brand">
              Terms of Use
            </Text>
            <Text textStyle="body-1">Updated as of {updatedDate}</Text>
          </Box>
        </Box>
      </PageSection>
      <PageSection backgroundColor="base.canvas.default">
        <VStack
          paddingTop="72px"
          paddingBottom="124px"
          marginX={{ base: '20px', md: '30px', lg: '55px', xl: '100px' }}
          as="section"
          position="relative"
          sx={{
            li: {
              marginTop: '32px',
            },
            'li li': {
              marginTop: '16px',
            },
            'li p': {
              marginTop: '16px',
            },
            div: {
              width: '100%',
            },
            '.legal': {
              'counter-reset': 'section',
            },
            '.legal ol': {
              'counter-reset': 'clause',
              'list-style': 'none outside none',
            },
            '.legal ol li': {
              'counter-increment': 'clause',
              'list-style': 'none outside none',
            },
            '.legal ol li:before': {
              content: `counters(clause,".") ". "`,
              position: 'absolute',
              left: '-50px',
            },
            '.legal ol li li li:before': {
              content: `counters(clause,".") ". "`,
              position: 'absolute',
              left: '0px',
            },
            '.legal ol li li li': {
              marginLeft: '72px',
            },
            '.specialgroup span': {
              position: 'absolute',
              left: '-50px',
            },
            '.specialgroup div': {
              marginLeft: '70px',
            },
            '.specialgroup div span': {
              position: 'absolute',
              left: '-70px',
            },
            '.specialgroup p': {
              marginTop: '16px',
            },
            '.specialgroup': {
              marginTop: '16px',
            },
            '.legal > ol > .specialgroup': {
              marginTop: '32px',
            },

            '.schedule ol': {
              listStyleType: 'decimal',
              marginLeft: '0px',
            },
            '.schedule ol ol': {
              listStyleType: 'lower-alpha',
              marginLeft: '16px',
            },
            '.schedule ol ol ol': {
              listStyleType: 'lower-roman',
              marginLeft: '32px',
            },
            '.schedule ol ol ol ol': {
              listStyleType: 'decimal',
              marginLeft: '32px',
            },
          }}
        >
          <div className="legal">
            <ol>
              <li>
                <strong>General</strong>

                <ol>
                  <li>
                    These Terms of Use govern your access to and use of our
                    services, including the application (whether as software or
                    as a website or otherwise), its contents (including APIs, if
                    any), push notifications and all other accompanying
                    materials as identified in the Schedule below (collectively,
                    the <strong>“Service”</strong>).
                  </li>

                  <li>
                    This Service is provided to you by the Government Technology
                    Agency (<strong>“GovTech”</strong> ). GovTech’s office is
                    located at 10 Pasir Panjang Road, #10-01, Mapletree Business
                    City, Singapore 117438.
                  </li>

                  <li>
                    By accessing or using any part of this Service, you
                    unconditionally agree and accept to be legally bound by
                    these Terms of Use and any amendments thereto from time to
                    time. GovTech reserves the right to change these Terms of
                    Use at its sole discretion and at any time.{' '}
                    <strong>
                      Please read the Terms of Use carefully each time you
                      access or use any part of this Service as (without
                      prejudice to any other means your agreement to the Terms
                      of Use as amended may manifest) such access or use shall
                      constitute your agreement to the Terms of Use and any
                      amendments to it.
                    </strong>{' '}
                    GovTech may, at its sole discretion and without prejudice to
                    its other rights under this Clause 1.3, also amend these
                    Terms of Use by providing you with notice effective
                    immediately or such other time designated by GovTech, and
                    such notice may be provided by any means GovTech deems
                    appropriate (for example, by posting the notice through the
                    Service, any website related to the Service, or by email).
                  </li>

                  <li>
                    <strong>
                      If you do not agree to these Terms of Use, please do not
                      use this Service or any part of this Service.
                    </strong>
                  </li>

                  <li>
                    If you are accessing or using the Service for and on behalf
                    of another entity (such as your employer), you warrant and
                    represent that you have the necessary authority to bind such
                    entity to these Terms of Use.
                  </li>
                </ol>
              </li>

              <li>
                <strong>Nature of this Service</strong>

                <p>
                  Please see the Schedule for more information and terms
                  concerning this Service.
                </p>
                <p>
                  The Service consists of work-in-progress hackathon projects
                  for OGP’s Hack for Public Good. The Services are in beta and
                  made available only for trial and feedback purposes. None of
                  the Services are for production use and, at GovTech’s sole and
                  absolute discretion, may receive no or limited support.
                  Certain functions may be disabled or simulated. You shall not
                  depend on any of the functions of any of the Services and,
                  without prejudice to GovTech’s other rights, GovTech disclaims
                  all liability arising out of or in connection with your use of
                  the Services.
                </p>
                <p>
                  This Service reproduces parts of Singapore legislation to
                  facilitate the search of such legislation. Singapore
                  legislation is subject to copyright of the Singapore
                  Government and is used/reproduced for the purposes of this
                  Service with the permission of the
                  Attorney&#8209;General&apos;s Chambers. Users of this Service
                  may check{' '}
                  {
                    <Link href="https://sso.agc.gov.sg/">
                      Singapore Statutes Online
                    </Link>
                  }{' '}
                  for the latest version of Singapore legislation.
                </p>
              </li>

              <li>
                <strong>Licence Terms and Restrictions</strong>

                <ol>
                  <li>
                    The Service, including the materials made available on or
                    through the Service, is owned by, licensed to, managed or
                    controlled by GovTech. Please see clause 4 (Third Party
                    Materials) for more information.
                  </li>

                  <li>
                    Subject to these Terms of Use, GovTech grants to you a
                    non-exclusive, revocable, and non-transferable right to
                    access and use the Service for personal or internal purposes
                    only, and only for such use permitted by the functions of
                    the Service and intended by GovTech.
                  </li>

                  {/* this is the special 3.2A/B group */}
                  <div className="specialgroup">
                    <span>3.2A.</span>You shall not, and shall not authorise or
                    permit any third party to:
                    <div style={{ position: 'relative' }}>
                      <p>
                        <span>3.2A.1.</span> bypass or circumvent any technical
                        restrictions or digital protection measures in the
                        Service or attempt to circumvent any such restrictions;
                      </p>
                      <p>
                        <span>3.2A.2.</span> reverse engineer, decompile,
                        disassemble, modify, translate, adapt or create
                        derivative works of the Service (whether in relation to
                        its source code, object code, underlying structure,
                        ideas, algorithms or otherwise);
                      </p>
                      <p>
                        <span>3.2A.3.</span> reproduce, publish, distribute,
                        transfer, publicly display, resell, rent, lease, or
                        sublicense the Service, or loan, lend, pledge, assign,
                        or otherwise encumber the Service to or in favour of any
                        third party;
                      </p>
                      <p>
                        <span>3.2A.4.</span> remove or obscure the copyright,
                        trademark and other proprietary notices contained on or
                        in the Service;
                      </p>
                      <p>
                        <span>3.2A.5.</span> use the Service in any manner that
                        is contrary to any applicable laws or regulations or
                        rights of third parties (however arising and of whatever
                        nature), or in a manner that constitutes harmful,
                        fraudulent, or obscene activity;
                      </p>
                      <p>
                        <span>3.2A.6.</span> make the Service available in or
                        through a network, file-sharing service, service bureau
                        or any similar timesharing arrangement or as a managed
                        service provider;
                      </p>
                      <p>
                        <span>3.2A.7.</span> perform any benchmarking tests or
                        analyses of the Service;
                      </p>
                      <p>
                        <span>3.2A.8.</span> use the Service to create anything
                        that would compete with the Service;
                      </p>
                      <p>
                        <span>3.2A.9.</span> transfer, assign or permit the
                        sharing of license keys to or with a third party;
                      </p>
                      <p>
                        <span>3.2A.10.</span> use the Service to process or
                        permit to be processed any code of a third party;
                      </p>
                      <p>
                        <span>3.2A.11.</span> provide third party access to the
                        Service; or
                      </p>
                      <p>
                        <span>3.2A.12.</span> export the Service in violation of
                        any international sanctions or laws applicable to US
                        entities.
                      </p>
                    </div>
                    <p>
                      <span>3.2B.</span>All express or implied rights to the
                      Service not specifically granted herein are expressly
                      reserved to GovTech.
                    </p>
                  </div>
                  {/* <!-- this is the special 3.2A/B group --> */}

                  <li>
                    GovTech reserve the right to:
                    <ol>
                      <li>Update or modify this Service from time to time;</li>
                      <li>
                        Deny or restrict access to or use of the Service by any
                        particular person without ascribing any reasons
                        whatsoever; and
                      </li>
                      <li>
                        Discontinue or terminate this Service at any time
                        without notice or liability to you whatsoever, whereupon
                        all rights granted to you hereunder shall also terminate
                        forthwith. You shall further upon notice from GovTech
                        return or destroy all copies of the Service or materials
                        therein that you may have been provided with.
                      </li>
                    </ol>
                  </li>

                  <li>
                    You will not interfere or attempt to interfere with the
                    proper working of the Service or otherwise do anything that
                    imposes an unreasonable or disproportionately large load on
                    GovTech’s servers.
                  </li>
                </ol>
              </li>

              {/* this is the special 3A group */}
              <div className="specialgroup">
                <strong>
                  <span>3A.</span>Account Access and Security
                </strong>

                <p>
                  <span>3A.1</span>You are solely responsible for maintaining
                  the confidentiality and security of any authentication
                  credentials associated with your use of the Service, including
                  the security of any of your devices which store the
                  authentication credentials.
                </p>
                <p>
                  <span>3A.2</span>GovTech shall be entitled, but not obliged,
                  to verify the identity of the person using the Service.
                  Without prejudice to the foregoing, GovTech is not under any
                  duty to verify that any biometric identifier used with the
                  Service, or on your device, belongs to you.
                </p>
                <p>
                  <span>3A.3</span>GovTech shall have the sole and absolute
                  discretion to invalidate any authentication credentials at any
                  time, or require you to have to re-authenticate or refresh
                  your authentication credentials at any time, without having to
                  give any reason for the same.
                </p>
                <p>
                  <span>3A.4</span>GovTech shall be entitled, but not obliged,
                  to act upon or rely on any instructions, information,
                  transmissions of data, or communications received from the
                  account or use of the Service in relation to your
                  authentication credentials, as if such instructions,
                  information, data or communications were issued by you,
                  whether or not the same was authorized by you.
                </p>
                <p>
                  <span>3A.5</span>For the avoidance of doubt, you are solely
                  responsible for any loss of whatever nature arising from
                  unauthorized or unofficial modifications made to your device
                  which permit or escalate privileged access, or remove
                  restrictions to such access, which are not intended by the
                  manufacturer or provider of your device or operating system of
                  your device (e.g., “rooting” or “jailbreaking” your mobile
                  phone).
                </p>
              </div>
              {/* <!-- this is the special 3A group --> */}

              <li>
                <strong>Third Party Materials</strong>

                <ol>
                  <li>
                    The Service may require, enable or facilitate access to or
                    use of software or services of a third party (“
                    <strong>Third Party</strong>”). In such an event, there may
                    be terms of use of the third party software or service (the
                    “<strong>Third Party Terms</strong>”). GovTech may be
                    required under or as a result of the Third Party Terms to
                    notify you of certain terms that apply to you (either
                    directly as an end user, or as a party whose acts or
                    omissions could cause GovTech to breach the Third Party
                    Terms) when you use the Services. An example of Third Party
                    Terms may be open source software terms or standard form
                    terms of the distribution platform from which you obtain any
                    part of the Service (e.g. Google Play Store or Apple App
                    Store terms) which bind GovTech as a developer or user of
                    the distribution platform (the “
                    <strong>Distribution Terms</strong>”). Information on the
                    Third Party Terms are embedded in the Service, already
                    accounted for in these Terms of Use, publicly available (e.g
                    the Distribution Terms) or otherwise indicated in the
                    Schedule herein. For the avoidance of doubt, insofar as this
                    Clause 4 relates to the Distribution Terms, the relevant
                    Distribution Terms are the terms of the specific platform
                    from which you obtained a copy of the software or
                    application that is part of the Service. For example, if you
                    obtained the said copy from the Google Play Store, then the
                    relevant terms are Google’s Distribution Terms.
                  </li>

                  <li>
                    <strong>
                      It is your responsibility to check and read the most
                      up-to-date versions of these Third Party Terms and you are
                      deemed to have notice of the same.
                    </strong>{' '}
                    In particular, you are deemed to have notice of the Third
                    Party Terms that GovTech (under the Third Party Terms) is
                    required to notify you, and you unconditionally agree to be
                    bound by all the obligations in the Third Party Terms which
                    are applicable to you (whether as end user, or as a party
                    whose acts or omissions could cause GovTech to breach the
                    Third Party Terms, or otherwise). For the avoidance of
                    doubt, where Third Party Terms are listed, such Third Party
                    Terms shall be deemed to include any privacy policies and
                    acceptable use policies as are applicable to you.
                  </li>

                  <li>
                    If the Third Party Terms require you to enter into an
                    agreement directly with the Third Party, then you
                    unconditionally agree to enter into such agreement, and in
                    any event, to be legally bound by the Third Party Terms. For
                    the avoidance of doubt:
                    <ol>
                      <li>
                        some Third Party Terms (particularly open-source terms)
                        permit either a direct licence to you from the Third
                        Party or a sublicence from GovTech to you. In such
                        cases, your licence is a direct licence from the Third
                        Party to you; and
                      </li>

                      <li>
                        the terms of your agreement with the Third Party will
                        govern your use of the relevant third party software or
                        service, and not these Terms of Use.
                      </li>
                    </ol>
                  </li>

                  <li>
                    If the Third Party Terms expressly or impliedly require
                    GovTech to incorporate certain terms in these Terms of Use
                    (inclusive of terms which impose any minimum or maximum
                    standards herein, and/or terms described in Clause 4.5
                    below), such terms are deemed to have been so incorporated
                    (the “<strong>Incorporated Terms</strong>”). Examples of
                    Incorporated Terms include provisions which require GovTech
                    to give you notice of certain rights and liabilities or
                    require GovTech to ensure that you acknowledge certain
                    matters. Similarly, if the Third Party Terms expressly or
                    impliedly require these Terms of Use to be altered such that
                    the Third Party Terms are complied with, the parties herein
                    agree that the Terms of Use shall be deemed to be so altered
                    but only to the extent necessary for compliance.
                  </li>

                  <li>
                    Some Third Party Terms grant the Third Party, or require
                    GovTech to grant the Third Party, direct rights of
                    enforcement of these Terms of Use as a third party
                    beneficiary, against you. Such Third Party Terms are deemed
                    to have been incorporated into these Terms of Use as
                    Incorporated Terms, and you hereby agree to grant such Third
                    Party, such direct rights of enforcement against you.
                  </li>

                  <div className="specialgroup">
                    {/* this is the special 4.5A group */}
                    <span>4.5A.</span>Unless the applicable Third Party Terms
                    permit you to commence legal proceedings against the
                    relevant Third Party, you shall not threaten or commence
                    legal proceedings against a Third Party without GovTech’s
                    prior written approval.
                    {/* <!-- this is the special 4.5A group --> */}
                  </div>

                  <li>
                    For the avoidance of doubt, without prejudice to Clause 4.4,
                    to the extent of any inconsistency between these Terms of
                    Use and the Third Party Terms, the latter shall prevail
                    provided nothing in the Third Party Terms increases the
                    liability of GovTech beyond that stated in Clause 6.
                  </li>
                  <li>
                    Without prejudice and in addition to the foregoing, GovTech
                    shall not be responsible for your use of any software or
                    service of a Third Party.
                  </li>
                </ol>
              </li>

              <li>
                <strong>
                  Your Consent to Your Data and to Access Functions of Your
                  Device
                </strong>
                <ol>
                  <li>
                    You hereby grant to GovTech a non-exclusive, worldwide,
                    perpetual and royalty-free right to collect, use, disclose,
                    process, modify, adapt, create derivative works of,
                    reproduce, and sublicense any and all information or data
                    submitted, uploaded or shared by you to the extent necessary
                    to provide the Service or for any other purpose expressly or
                    impliedly provided in these Terms of Use, or as permitted by
                    law.
                  </li>
                  <li>
                    Use of the Service may require you to allow access by the
                    Service to certain functions of your device, such as push
                    notifications, the obtaining and/or sharing of your
                    location, or the collection of data from you in connection
                    with the Service. Your use of the Service shall constitute
                    your consent to the access by the Service of such functions
                    of your device as may be reasonably required by the Service.
                  </li>
                  <li>
                    You further irrevocably and unconditionally waive, and shall
                    cause to be irrevocably and unconditionally waived, all
                    existing and future moral rights (including the right of
                    identification) wherever in the world in respect of any
                    information or data submitted, uploaded or shared by you
                    (including feedback, requests or suggestions concerning the
                    Services) to GovTech. Such waiver shall also extend to
                    GovTech’s licensees, assigns and successors-in-title.
                  </li>
                  <li>Please also see clause 8 (Privacy Statement).</li>
                </ol>
              </li>

              {/* this is the special 5A group */}
              <div className="specialgroup">
                <strong>
                  <span>5A.</span>Ownership of Feedback/Requests/Suggestions
                </strong>
                <p>
                  You agree that all title and interest in any feedback,
                  requests or suggestions from you concerning the Services
                  provided to GovTech shall be owned by GovTech and, without
                  prejudice and in addition to clause 5.3, you shall waive all
                  rights existing in or in respect of the same (including, for
                  the avoidance of doubt, any signature requirements).
                </p>
              </div>
              {/* <!-- this is the special 5A group --> */}

              {/* this is the special 5B group */}
              <div className="specialgroup">
                <strong>
                  <span>5B.</span>Confidentiality
                </strong>
                <p>
                  <span>5B.1</span> If you receive information or data (in
                  whatever form) from GovTech or a Third Party which is
                  designated confidential or proprietary or is otherwise
                  reasonably understood to be confidential or proprietary
                  (collectively, “<strong>Confidential Information</strong>
                  ”), you shall not use, disclose or reproduce the Confidential
                  Information except for the purpose for which it was provided
                  to you. If consent to disclose the Confidential Information to
                  a third party is given by GovTech or the Third Party to you,
                  any act or omission in respect of the Confidential Information
                  by that person shall be deemed to be your act or omission and
                  you agree to be fully liable for the same. In all cases, you
                  shall protect the Confidential Information to the same extent
                  you protect your own confidential information but in no event
                  less than a reasonable standard of care. You shall ensure that
                  any recipients are bound by confidentiality terms at least as
                  restrictive as this Clause.
                </p>
                <p>
                  <span>5B.2</span>You shall destroy any Confidential
                  Information immediately upon request by GovTech or the Third
                  Party.
                </p>
                <p>
                  <span>5B.3</span>In the event:
                </p>
                <div style={{ position: 'relative' }}>
                  <p>
                    <span>5B.3.1.</span>you are, or likely to be, required by an
                    order of court to disclose Confidential Information; or
                  </p>
                  <p>
                    <span>5B.3.2.</span>you have reasonable grounds to suspect
                    the unauthorised use or disclosure or reproduction of
                    Confidential Information;
                  </p>
                </div>
                <p>
                  you shall immediately notify GovTech or the Third Party of the
                  same and cooperate with GovTech or the Third Party to prevent
                  or limit such disclosure.
                </p>
                <p>
                  <span>5B.4.</span> Nothing in this Clause 5B shall prejudice
                  GovTech’s or the Third Party’s other rights at law.
                </p>
              </div>
              {/* <!-- this is the special 5B group --> */}

              <li>
                <strong>Disclaimers and Indemnity</strong>

                <ol>
                  <li>
                    <strong>
                      The Service is provided on an “as is” and “as available”
                      basis without warranties of any kind. To the fullest
                      extent permitted by law, GovTech does not make any
                      representations or warranties of any kind whatsoever in
                      relation to the Service and hereby disclaims all express,
                      implied and/or statutory warranties of any kind to you or
                      any third party, whether arising from usage or custom or
                      trade or by operation of law or otherwise, including but
                      not limited to any representations or warranties:
                    </strong>

                    <ol>
                      <li>
                        <strong>
                          as to the accuracy, completeness, correctness,
                          currency, timeliness, reliability, availability,
                          interoperability, security, non-infringement, title,
                          merchantability, quality or fitness for any particular
                          purpose of the Service; and/or
                        </strong>
                      </li>

                      <li>
                        <strong>
                          that the Service or any functions associated therewith
                          will be uninterrupted or error-free, or that defects
                          will be corrected or that this Service, website and
                          the server are and will be free of all viruses and/or
                          other malicious, destructive or corrupting code,
                          programme or macro.
                        </strong>
                      </li>
                    </ol>
                  </li>

                  <li>
                    <strong>
                      GovTech shall also not be liable to you or any third party
                      for any damage or loss of any kind whatsoever and
                      howsoever caused, including but not limited to any direct
                      or indirect, special or consequential damages, loss of
                      income, revenue or profits, lost or damaged data, or
                      damage to your computer, software or any other property,
                      whether or not arising directly or indirectly from –
                    </strong>

                    <ol>
                      <li>
                        <strong>
                          your access to or use of this Service, or any part
                          thereof;
                        </strong>
                      </li>
                      <li>
                        <strong>
                          any loss of access or use of this Service or any part
                          of this Service, howsoever caused;
                        </strong>
                      </li>
                      <li>
                        <strong>
                          any inaccuracy or incompleteness in, or errors or
                          omissions in the transmission of, the Service;
                        </strong>
                      </li>
                      <li>
                        <strong>
                          any delay or interruption in the transmission of the
                          Service, whether caused by delay or interruption in
                          transmission over the internet or otherwise; or
                        </strong>
                      </li>
                      <li>
                        <strong>
                          any decision made or action taken by you or any third
                          party in reliance upon the Service,
                        </strong>
                      </li>
                    </ol>
                    <p>
                      <strong>
                        regardless of whether GovTech has been advised of the
                        possibility of such damage or loss.
                      </strong>
                    </p>
                  </li>

                  <li>
                    <strong>
                      Without prejudice and in addition to the foregoing,
                      insofar as the Service facilitates or requires the
                      provision, use or functioning of, or is provided in
                      conjunction with, other products, software, materials
                      and/or services not provided by GovTech, GovTech makes no
                      representation or warranty in relation to such products,
                      software, materials and/or services (including without
                      limitation any representation or warranties as to
                      timeliness, reliability, availability, interoperability,
                      quality, fitness for purpose, non-infringement,
                      suitability or accuracy).
                    </strong>
                  </li>

                  <li>
                    You shall not rely on any part of the Service to claim or
                    assert any form of legitimate expectation against GovTech,
                    whether or not arising out of or in connection with
                    GovTech’s roles and functions as a public authority. GovTech
                    shall have no responsibility or liability to you or any
                    third party arising out of or in connection with any fraud,
                    phishing, or any other illegal act or omission by other
                    parties in relation to the Service and it is your own
                    responsibility to ensure that the Service you are using or
                    accessing is from a legitimate source.
                  </li>

                  <li>
                    <strong>
                      You agree to defend and indemnify and keep GovTech and its
                      officers, employees, agents and contractors harmless
                      against all liabilities, losses, damages, costs or
                      expenses (including legal costs on an indemnity basis)
                      howsoever arising out of or in connection with your access
                      or use of the Service (including third party software or
                      services) or your non-compliance with the Terms of Use,
                      Third Party Terms or Incorporated Terms, whether or not
                      you had been advised or informed of the nature or extent
                      of such liabilities, losses, damages, costs or expenses.
                      You warrant and represent that your access or use of the
                      Service does not and will not breach or violate any laws,
                      regulations, trade, economic and/or export sanctions
                      (wherever in the world) applicable to you, and that you
                      shall not transmit any malicious code, illegal, infringing
                      or undesirable content or materials to GovTech or its
                      agents or any Third Party.
                    </strong>
                  </li>

                  <li>
                    GovTech shall have the right to take any and all necessary
                    actions/omissions to protect its interests, including
                    complying with any legal requirements (such as taking down,
                    disabling and disabling access to, removing (permanently or
                    temporarily), and/or restoring (including restoring access
                    to) any materials contained in, accessed through, uploaded
                    to, and/or made available via the Service in response to any
                    take-down or restoration notices). You agree that GovTech is
                    not obliged to determine the merits of any take-down or
                    restoration notices. You further waive any rights arising as
                    a result of the actions/omissions taken by GovTech.
                  </li>

                  <li>
                    Without prejudice and in addition to GovTech’s other rights:
                    <ol>
                      <li>
                        in no event shall GovTech’s total cumulative liability
                        arising out of or in connection with these Terms of Use
                        to you exceed the amount of fees or payment received by
                        GovTech (and not paid or given to any Third Party by
                        GovTech) from you for the Service in the 12 months
                        preceding the date of the first cause of action; and
                      </li>
                      <li>
                        no action may be brought by you against GovTech arising
                        out of or in connection with these Terms of Use more
                        than one (1) year after the cause of action arose.
                      </li>
                    </ol>
                  </li>
                </ol>
              </li>

              <li>
                <strong>Hyperlinks</strong>

                <ol>
                  <li>
                    Insofar as the Service provides a hyperlink to material not
                    maintained or controlled by GovTech, GovTech shall not be
                    responsible for the content of the hyperlinked material and
                    shall not be liable for any damages or loss arising from
                    access to the hyperlinked material. Use of the hyperlinks
                    and access to such hyperlinked materials are entirely at
                    your own risk. The hyperlinks are provided merely as a
                    convenience to you and do not imply endorsement by,
                    association or affiliation with GovTech of the contents of
                    or provider of the hyperlinked materials.
                  </li>

                  <li>
                    Caching and hyperlinking to, and the framing of, any part of
                    the Service is prohibited save where you have obtained
                    GovTech’s prior written consent. Such consent may be subject
                    to any conditions as may be determined by GovTech in its
                    sole discretion. If you hyperlink to or frame any part of
                    the Service, that shall constitute your acceptance of these
                    Terms of Use and all amendments thereto. If you do not
                    accept these Terms of Use as may be amended from time to
                    time, you must immediately discontinue linking to or framing
                    of any part of the Service.
                  </li>

                  <li>
                    GovTech reserves all rights:
                    <ol>
                      <li>
                        to disable any links to, or frames of, any materials
                        which are unauthorised (including without limitation
                        materials which imply endorsement by or association or
                        affiliation with GovTech, materials containing
                        inappropriate, profane, defamatory, infringing, obscene,
                        indecent or unlawful topics, names, or information that
                        violates any written law, any applicable intellectual
                        property, proprietary, privacy or publicity rights); and
                      </li>

                      <li>
                        to disclaim responsibility and/or liability for
                        materials that link to or frame any part of the Service.
                      </li>
                    </ol>
                  </li>
                </ol>
              </li>

              <li>
                <strong>Privacy Statement</strong>

                <p>
                  You also agree to the terms of the Government Agency Privacy
                  Statement for this Service as may be amended from time to
                  time. The Government Agency Privacy Statement will form part
                  of these Terms of Use.
                </p>
              </li>

              <li>
                <strong>Rights of Third Parties</strong>

                <p>
                  Subject to the rights of the Third Party and/or Singapore
                  public sector agencies, a person who is not a party to this
                  Terms of Use shall have no right under the Contract (Rights of
                  Third Parties) Act or otherwise to enforce any of its terms.
                  Variation or rescission of these Terms of Use shall not
                  require the consent of any third party, including any Third
                  Party and/or other Singapore public sector agencies.
                </p>
              </li>

              <li>
                <strong>Assignment</strong>

                <ol>
                  <li>
                    You may not assign or sub-contract this Terms of Use without
                    the prior written consent of GovTech.
                  </li>

                  <li>
                    GovTech may assign, novate, transfer, or sub-contract the
                    rights and liabilities in respect of the Service and this
                    Terms of Use, without notifying you and without further
                    reference to you. Your acceptance of this Terms of Use shall
                    also constitute your consent to such assignment, novation,
                    transfer or sub-contract.
                  </li>
                </ol>
              </li>

              {/* this is the special 10A/B/C group */}
              <div className="specialgroup">
                <strong>
                  <span>10A.</span> Severability
                </strong>
                <p>
                  If any term of these Terms of Use is held by a court or
                  tribunal of competent jurisdiction to be invalid or
                  unenforceable, then these Terms of Use, including all of the
                  remaining terms, will remain in full force and effect as if
                  such invalid or unenforceable term had never been included
                  but, to the extent permissible, such invalid or unenforceable
                  terms shall be deemed to have been replaced by terms that are
                  (a) valid and enforceable and (b) express the intention or
                  produce the result closest to the original intention of the
                  invalid or unenforceable terms.
                </p>
              </div>
              <div className="specialgroup">
                <strong>
                  <span>10B.</span> Order of Precedence
                </strong>
                <p>
                  In the event of any conflict, inconsistency or ambiguity
                  between or in any one or more terms in these Terms of Use,
                  such conflict, inconsistency or ambiguity shall be resolved in
                  favour of GovTech and the provision or interpretation which is
                  more favourable to GovTech shall prevail. Notwithstanding any
                  other term, GovTech has the sole and absolute discretion to
                  determine which term or interpretation is more favourable to
                  it and such decision shall be binding on you.
                </p>{' '}
              </div>
              <div className="specialgroup">
                <strong>
                  <span>10C.</span> Entire Agreement
                </strong>
                <p>
                  These Terms of Use contain the entire and whole agreement
                  concerning the subject matter of these Terms of Use. The Terms
                  of Use supersedes all prior written or oral representations,
                  agreements and/or understandings between GovTech and yourself.
                  Except for amendments by GovTech under these Terms of Use, no
                  amendment to these Terms of Use shall be of any force unless
                  agreed upon in writing by both parties.
                </p>{' '}
              </div>
              <div className="specialgroup">
                <strong>
                  <span>10D.</span> Waiver
                </strong>
                <div style={{ position: 'relative' }}>
                  <p>
                    <span>10D.1.</span>
                    Any delay, failure or omission on the part of GovTech in
                    enforcing any right, power, privilege, claim or remedy (“
                    <strong>Remedy</strong>”), which is conferred under the
                    Terms of Use or at law or in equity, or arises from any
                    breach by you, shall not (a) be deemed to be or be construed
                    as a waiver or variation of the Remedy, or of any other such
                    Remedy, in respect of the particular circumstances in
                    question, or (b) operate so as to bar the enforcement or
                    exercise of the Remedy, or of any other such Remedy in any
                    other subsequent instances.
                  </p>
                  <p>
                    <span>10D.2.</span>
                    No waiver by GovTech of any breach of the Terms of Use by
                    you shall be deemed to be a waiver of any other or of any
                    subsequent breach.
                  </p>
                  <p>
                    <span>10D.3.</span>
                    Any waiver by GovTech granted under the Terms of Use must be
                    in writing and may be given subject to conditions. Such
                    waiver under the Terms of Use shall be effective only in the
                    instance and for the purpose for which it is given.
                  </p>
                </div>
              </div>

              <li>
                <strong>Governing Law and Dispute Resolution</strong>

                <ol>
                  <li>
                    These Terms of Use shall be governed by and construed in
                    accordance with laws of Singapore.
                  </li>

                  <li>
                    Subject to clause 11.3, any dispute arising out of or in
                    connection with these Terms of Use, including any question
                    regarding its existence, validity or termination, shall be
                    referred to and finally resolved in the Courts of the
                    Republic of Singapore and the parties hereby submit to the
                    exclusive jurisdiction of the Courts of the Republic of
                    Singapore.
                  </li>

                  <li>
                    GovTech may, at its sole discretion, refer any dispute
                    referred to in clause 11.2 above to arbitration administered
                    by the Singapore International Arbitration Centre (“
                    <strong>SIAC</strong>”) in Singapore in accordance with the
                    Arbitration Rules of the SIAC (“<strong>SIAC Rules</strong>
                    ”) for the time being in force, which rules are deemed to be
                    incorporated by reference in this clause. Further:
                    <ol>
                      <li>The seat of the arbitration shall be Singapore.</li>

                      <li>The tribunal shall consist of one (1) arbitrator.</li>

                      <li>The language of the arbitration shall be English.</li>

                      <li>
                        All information, pleadings, documents, evidence and all
                        matters relating to the arbitration shall be
                        confidential.
                      </li>
                    </ol>
                    <p>
                      Where GovTech is the defendant or respondent, it shall be
                      given at least 30 days before the commencement of any
                      legal action against it to elect to exercise the right
                      herein to have the dispute submitted to arbitration. This
                      right to elect shall not prejudice GovTech’s right to a
                      limitation defence and the period to exercise the right
                      shall not be abridged by reason of any accrual of a
                      limitation defence in favour of GovTech during the said
                      period.
                    </p>
                  </li>
                </ol>
              </li>

              <p style={{ textAlign: 'center', marginTop: '100px' }}>
                These Terms of Use are dated {updatedDate}.
              </p>
            </ol>
          </div>

          <Text textStyle="h3" textAlign="center" paddingTop="80px">
            Schedule
          </Text>

          <div className="schedule">
            <ol>
              <li>
                <strong>Name of Service:</strong> Pair Search
              </li>

              <li>
                <strong>Nature of Service</strong>
                <ol type="a">
                  <li>
                    This Service facilitates search of publicly available data,
                    including but not limited to the Official Reports of the
                    Singapore Parliamentary Debates (Hansard), court judgments
                    from the Supreme Court of Singapore, and Singapore
                    legislation from Singapore Statutes Online.
                  </li>
                </ol>
              </li>
            </ol>
          </div>
        </VStack>
      </PageSection>
    </>
  )
}
